/**
 * Geometry of the screen-share annotation overlay.
 *
 * Everything that goes over the wire is expressed relative to the video
 * *frame*, never to the DOM element. Participants have differently sized
 * windows and the <video> is rendered with `object-fit: contain`, so the
 * element is almost always taller or wider than the picture inside it, with
 * letterbox bars making up the difference. Anchoring to the element would put
 * every cursor in the wrong place for anyone whose window aspect ratio differs
 * from the frame's.
 */

/**
 * Frame coordinates are integers in [0, FRAME_UNIT] on both axes.
 *
 * 4095 (12 bits) is not arbitrary: on a 4K share one unit is under a pixel
 * horizontally, which is finer than a human can point, and the value still
 * fits in four characters of JSON.
 */
export const FRAME_UNIT = 4095;

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Visible picture inside an element rendered with `object-fit: contain`.
 *
 * Returned in the same coordinate space as `element` (viewport pixels when it
 * comes from getBoundingClientRect). Undefined while the video has no
 * dimensions yet — metadata arrives after the element does, and drawing
 * against a zero-sized frame would divide by zero and pile everything into the
 * corner.
 */
export function contentBox(
  element: Box,
  videoWidth: number,
  videoHeight: number,
): Box | undefined {
  if (videoWidth <= 0 || videoHeight <= 0) return undefined;
  if (element.width <= 0 || element.height <= 0) return undefined;

  const scale = Math.min(
    element.width / videoWidth,
    element.height / videoHeight,
  );

  const width = videoWidth * scale;
  const height = videoHeight * scale;

  return {
    left: element.left + (element.width - width) / 2,
    top: element.top + (element.height - height) / 2,
    width,
    height,
  };
}

/**
 * Viewport point to frame coordinates, or undefined when the point is outside
 * the picture (i.e. over a letterbox bar). Cursors use this: a pointer over the
 * black bars is not pointing at anything and should not be broadcast.
 */
export function toFrame(
  clientX: number,
  clientY: number,
  content: Box,
): Point | undefined {
  if (content.width <= 0 || content.height <= 0) return undefined;

  const x = ((clientX - content.left) / content.width) * FRAME_UNIT;
  const y = ((clientY - content.top) / content.height) * FRAME_UNIT;

  if (x < 0 || y < 0 || x > FRAME_UNIT || y > FRAME_UNIT) return undefined;

  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * Same conversion, but clamped to the frame instead of rejected.
 *
 * Drawing uses this: when a drag runs past the edge of the picture the stroke
 * should follow the border, not develop holes wherever the pointer strayed.
 */
export function toFrameClamped(
  clientX: number,
  clientY: number,
  content: Box,
): Point {
  if (content.width <= 0 || content.height <= 0) return { x: 0, y: 0 };

  return {
    x: clampFrame(((clientX - content.left) / content.width) * FRAME_UNIT),
    y: clampFrame(((clientY - content.top) / content.height) * FRAME_UNIT),
  };
}

/**
 * Frame coordinates back to pixels *local to the content box* — the overlay is
 * positioned over the picture, so both the ink canvas and the cursor elements
 * share this origin.
 */
export function fromFrame(point: Point, content: Box): Point {
  return {
    x: (point.x / FRAME_UNIT) * content.width,
    y: (point.y / FRAME_UNIT) * content.height,
  };
}

/**
 * Stroke width in pixels.
 *
 * Widths travel as a fraction of frame *height* for the same reason
 * coordinates do: a line authored at 4 px on a laptop would be a hairline on
 * someone else's 27-inch window. Never rounds down to nothing — a stroke that
 * renders as zero pixels looks like a bug, not like a thin line.
 */
export function strokeWidthPx(units: number, content: Box): number {
  return Math.max(1, (units / FRAME_UNIT) * content.height);
}

export function clampFrame(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(FRAME_UNIT, Math.max(0, Math.round(value)));
}
